import moment from "moment";
import { useState, useRef, useEffect, useMemo } from "react";

import { useSelector } from "react-redux";
import DatePicker from "react-datepicker";
import { useTranslation } from "react-i18next";

import calendar from "../../../assets/images/calendar-small.svg";
import crossIcon from "../../../assets/images/textarea-cross-button.svg";

import Spinner from "components/spinner";

import { localeForDatePicker } from "../../../utils/language";

const mapGenderAtBirth = (genderId) => {
  const x = String(genderId ?? "").trim();
  if (x === "1") return "male";
  if (x === "2") return "female";
  return "";
};

const parseHeightCm = (pHome) => {
  const raw =
    pHome?.height_cm ?? pHome?.height ?? pHome?.stature_cm ?? pHome?.stature;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : "";
};

// need to to add the module to the seed data in BE because id is mutable atm
// const isRofVideoById = (modules) => {
//   if (!modules) return false;
//   const arr = Array.isArray(modules) ? modules : [modules];
//   return arr.some((m) => m?.id === ROF_VIDEO_MODULE_ID);
// };

const isRofVideoByName = (moduleName) => {
  if (!moduleName) return false;
  const name = moduleName.toLowerCase();
  return (
    name.includes("risk of fall (video assessment") ||
    name.includes("risco de queda (avaliação por vídeo)")
  );
};

// Patient age helper
const calcPatientAge = (birthdate) => {
  if (!birthdate) return null;

  const d = moment(birthdate);
  if (!d.isValid()) return null;
  return moment().diff(d, "years");
};

const CreateEvaluationModal = ({
  patientId,
  prefillClinical,
  patientBirthdate,
  onCancel,
  onSubmit,
  isButtonDisabled,
  canAssignFallRisk = false,
  availableModules = [],
  loadAvailableModules = () => Promise.resolve(),
}) => {
  const { t, i18n } = useTranslation();
  const datePickerInput = useRef(null);

  const evaluationTags = useSelector((state) => state.evaluationTags);
  const prefillGender = mapGenderAtBirth(prefillClinical?.gender_id);
  const prefillHeight =
    prefillClinical?.height != null
      ? String(Math.round(prefillClinical.height))
      : "";

  const patientAge = useMemo(
    () => calcPatientAge(patientBirthdate),
    [patientBirthdate],
  );

  const isUnder60 = patientAge != null && patientAge < 60;

  const [rofGenderAtBirth, setRofGenderAtBirth] = useState(prefillGender);
  const [rofHeightCm, setRofHeightCm] = useState(prefillHeight);
  const [rofDirty, setRofDirty] = useState({ gender: false, height: false });
  const [selectedFallModuleId, setSelectedFallModuleId] = useState(null);
  const [isNoteFieldHide, setIsNoteFieldHide] = useState(false);
  const [newEvaluationInfo, setNewEvaluationInfo] = useState({
    name: t("patient.evaluations.new_evaluation"),
    selectedTagsIds: [],
    date: new Date(),
    notes: "",
  });

  const selectedModule = useMemo(
    () => availableModules.find((m) => m.id === selectedFallModuleId) || null,
    [availableModules, selectedFallModuleId],
  );

  const showRofInputs = useMemo(() => {
    if (!canAssignFallRisk) return false;
    return isRofVideoByName(selectedModule?.name);
  }, [canAssignFallRisk, selectedModule]);

  const effectiveGender = rofGenderAtBirth || prefillGender;
  const effectiveHeight =
    String(rofHeightCm).trim() !== "" ? Number(rofHeightCm) : prefillHeight;

  const rofHeightIsValid = !showRofInputs || Number(effectiveHeight) > 0;
  const rofGenderIsValid =
    !showRofInputs ||
    effectiveGender === "male" ||
    effectiveGender === "female";

  const createDisabled =
    isButtonDisabled || !(rofHeightIsValid && rofGenderIsValid);

  const textInputsHandler = (e) => {
    setNewEvaluationInfo({
      ...newEvaluationInfo,
      [e.target.name]: e.target.value,
    });
  };

  const tagSelectHandler = (tagId) => {
    let tagIdArray = [...newEvaluationInfo.selectedTagsIds];
    const index = tagIdArray.indexOf(tagId);

    if (index > -1) {
      tagIdArray.splice(index, 1);
    } else {
      tagIdArray = [...tagIdArray, tagId];
    }

    setNewEvaluationInfo({ ...newEvaluationInfo, selectedTagsIds: tagIdArray });
  };

  const changeDate = (newDate) => {
    setNewEvaluationInfo({ ...newEvaluationInfo, date: newDate });
  };

  useEffect(() => {
    setRofDirty({ gender: false, height: false });

    setRofGenderAtBirth(prefillGender);
    setRofHeightCm(prefillHeight);

    setIsNoteFieldHide(false);
    setNewEvaluationInfo({
      name: t("patient.evaluations.new_evaluation"),
      selectedTagsIds: [],
      date: new Date(),
      notes: "",
    });

    setSelectedFallModuleId(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId, prefillGender, prefillHeight]);

  useEffect(() => {
    if (!showRofInputs) return;
    if (!rofDirty.gender) setRofGenderAtBirth(prefillGender);
    if (!rofDirty.height) setRofHeightCm(prefillHeight);
  }, [
    showRofInputs,
    prefillGender,
    prefillHeight,
    rofDirty.gender,
    rofDirty.height,
  ]);

  useEffect(() => {
    if (canAssignFallRisk && availableModules?.length > 0) {
      setSelectedFallModuleId((prev) => {
        // If current selection is valid, keep it
        if (prev != null) {
          const curr = availableModules.find((m) => m.id === prev);
          if (curr && !(isUnder60 && isRofVideoByName(curr?.name))) return prev;
        }
        // Otherwise pick the first allowed module
        const firstAllowed = availableModules.find(
          (m) => !(isUnder60 && isRofVideoByName(m?.name)),
        );
        return firstAllowed ? firstAllowed.id : null;
      });
    }
  }, [canAssignFallRisk, availableModules, isUnder60]);

  const handleSubmit = () => {
    const fallRiskAttach = canAssignFallRisk
      ? { module_id: selectedFallModuleId }
      : null;

    const rofPatientOverrides = showRofInputs
      ? {
          gender_at_birth: effectiveGender || null,
          height_cm:
            Number(effectiveHeight) > 0 ? Number(effectiveHeight) : null,
        }
      : null;

    onSubmit(
      newEvaluationInfo.name,
      newEvaluationInfo.selectedTagsIds,
      moment(newEvaluationInfo.date).utc().format("YYYY-MM-DD HH:mm:ss"),
      newEvaluationInfo.notes,
      fallRiskAttach,
      rofPatientOverrides,
    );
  };

  return (
    <div className='create-eval-modal-wrapper'>
      <div className='create-eval-modal-shadow'>
        <div className='create-eval-modal-container'>
          <div className='title-text'>
            {t("patient.evaluations.new_evaluation")}
            <img
              alt='Close Programs Filter'
              className='programs-filter-close-btn'
              src={crossIcon}
              style={{
                width: 14,
                height: 14,
                display: "inline-block",
                float: "right",
                cursor: "pointer",
              }}
              onClick={onCancel}
            />
          </div>

          {/* Name */}
          <label className='input-label-text' htmlFor='name'>
            {t("patient.evaluations.name")}
          </label>
          <input
            autoComplete='off'
            onChange={textInputsHandler}
            value={newEvaluationInfo.name}
            className='form-input'
            name='name'
            id='name'
            placeholder={t("placeholders.evaluation_name")}
          />

          {/* Module selector */}
          {canAssignFallRisk && (
            <>
              <label className='input-label-text'>
                {t("rof.evaluation_modules")}
              </label>
              <select
                className='form-input'
                value={selectedFallModuleId ?? ""}
                onChange={(e) =>
                  setSelectedFallModuleId(Number(e.target.value))
                }
                disabled={!canAssignFallRisk || availableModules.length === 0}
              >
                {availableModules.length === 0 ? (
                  <option value='' disabled>
                    {t("rof.loading_modules")}
                  </option>
                ) : (
                  availableModules.map((m) => {
                    const isRofVideo = isRofVideoByName(m?.name);
                    const disabled = isUnder60 && isRofVideo;
                    const label = disabled
                      ? `${m.name} — ${t("rof.under60_hint")}`
                      : m.name;
                    return (
                      <option key={m.id} value={m.id} disabled={disabled}>
                        {label}
                      </option>
                    );
                  })
                )}
              </select>
              <button className='tertiary-button add-note-button' disabled>
                {t("rof.add_another_module")}
              </button>
            </>
          )}

          {/* ROF extra fields */}
          {showRofInputs && (
            <div className='mt-3'>
              <div className='row'>
                {/* Gender at birth */}
                <div className='col-12 col-md-6'>
                  <label className='input-label-text'>{t("rof.gender")}</label>
                  <select
                    className='form-input mb-0'
                    value={rofGenderAtBirth}
                    onChange={(e) => {
                      setRofDirty((s) => ({ ...s, gender: true }));
                      setRofGenderAtBirth(e.target.value);
                    }}
                  >
                    <option value=''>
                      {prefillGender
                        ? `${t("rof.select_gender")} (${t(
                            `rof.${prefillGender}`,
                          )})`
                        : t("rof.select_gender")}
                    </option>
                    <option value='female'>{t("rof.female")}</option>
                    <option value='male'>{t("rof.male")}</option>
                  </select>
                  {!rofGenderIsValid && (
                    <small className='text-danger input-label-text pt-0'>
                      {t("rof.required_field")}
                    </small>
                  )}
                </div>

                {/* Height (cm) */}
                <div className='col-12 col-md-6'>
                  <label className='input-label-text'>{t("rof.height")}</label>
                  <input
                    className='form-input mb-0'
                    type='number'
                    min={50}
                    max={260}
                    step='1'
                    value={rofHeightCm}
                    onChange={(e) => {
                      setRofDirty((s) => ({ ...s, height: true }));
                      setRofHeightCm(e.target.value);
                    }}
                    placeholder={
                      prefillHeight
                        ? String(prefillHeight)
                        : t("rof.enter_height")
                    }
                  />
                  {!rofHeightIsValid && (
                    <small className='text-danger input-label-text'>
                      {t("rof.required_field")}
                    </small>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Tags */}
          <label className='input-label-text'>
            {t("patient.evaluations.selected_tags")}
          </label>
          {evaluationTags && evaluationTags.length !== 0 && (
            <div className='tags-wrapper regular-text black-color pointer'>
              {evaluationTags.map((tag) => (
                <div
                  key={tag.id}
                  onClick={() => tagSelectHandler(tag.id)}
                  className={`tag-container ${
                    newEvaluationInfo.selectedTagsIds.includes(tag.id)
                      ? "active"
                      : ""
                  }`}
                >
                  {tag.name}
                </div>
              ))}
            </div>
          )}

          {/* Date */}
          <label className='input-label-text'>
            {t("patient.evaluations.date")}
          </label>
          <div style={{ position: "relative" }}>
            <DatePicker
              ref={datePickerInput}
              autoComplete='off'
              className='form-input w-100'
              name='date'
              id='date'
              placeholderText='yyyy-mm-dd hh-mm'
              locale={localeForDatePicker(i18n.language)}
              dateFormat='yyyy-MM-dd HH:mm'
              timeInputLabel='Time:'
              showTimeInput
              selected={newEvaluationInfo.date}
              onChange={(date) => changeDate(date)}
            />
            <img
              onClick={() => datePickerInput.current.setOpen(true)}
              alt='calendar'
              className='input-calendar'
              src={calendar}
            />
          </div>

          {/* Notes */}
          <label className='input-label-text' htmlFor='notes'>
            {t("patient.evaluations.notes")}
          </label>
          <div
            style={{
              position: "relative",
              display: isNoteFieldHide ? "block" : "none",
            }}
          >
            <textarea
              autoComplete='off'
              onChange={textInputsHandler}
              value={newEvaluationInfo.notes}
              className='form-input'
              name='notes'
              id='notes'
              placeholder={t("placeholders.notes")}
              rows='4'
            />
            <img
              src={crossIcon}
              alt='Close Notes Field'
              style={{
                position: "absolute",
                width: 9.33,
                height: 9.33,
                top: 11,
                right: 11,
                cursor: "pointer",
              }}
              onClick={() => {
                setIsNoteFieldHide(!isNoteFieldHide);
                setNewEvaluationInfo((prev) => ({ ...prev, notes: "" }));
              }}
            />
          </div>
          <div>
            <button
              className='tertiary-button add-note-button'
              style={
                isNoteFieldHide ? { display: "none" } : { display: "block" }
              }
              onClick={() => setIsNoteFieldHide(!isNoteFieldHide)}
            >
              {isNoteFieldHide
                ? t("patient.evaluations.cancel_notes")
                : t("patient.evaluations.add_notes")}
            </button>
          </div>

          {/* Footer */}
          <div className='footer-wrapper'>
            <button
              onClick={onCancel}
              className='small-button transparent-button'
            >
              <u>{t("buttons.cancel")}</u>
            </button>
            <button
              onClick={handleSubmit}
              className='small-button secondary-button'
              disabled={createDisabled}
            >
              {isButtonDisabled ? (
                <div className='position-relative p-1'>
                  <Spinner type={"small"} />
                </div>
              ) : (
                t("buttons.create_evaluation")
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateEvaluationModal;
